import React, { useEffect, useState, useCallback } from "react";
import { APIService } from "../../../shared/shared-with-mobile/api-client/api.service";
import {
  OutboundSendLogRow,
  OutboundSendEventRow,
  SendChannel,
  SendStatus,
  LogQueryParams,
} from "../../../shared/shared-with-mobile/api-client/services/communications.service";

const PAGE_SIZE = 50;

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  pending: { bg: "rgba(171, 171, 183, 0.18)", fg: "#666" },
  sent: { bg: "rgba(33, 150, 243, 0.18)", fg: "#1565c0" },
  delivered: { bg: "rgba(76, 175, 80, 0.18)", fg: "#2e7d32" },
  opened: { bg: "rgba(76, 175, 80, 0.32)", fg: "#1b5e20" },
  clicked: { bg: "rgba(76, 175, 80, 0.45)", fg: "#1b5e20" },
  bounced: { bg: "rgba(244, 67, 54, 0.18)", fg: "#c62828" },
  failed: { bg: "rgba(244, 67, 54, 0.18)", fg: "#c62828" },
  blocked_dev: { bg: "rgba(255, 152, 0, 0.18)", fg: "#cc7a00" },
  pending_unknown: { bg: "rgba(255, 152, 0, 0.18)", fg: "#cc7a00" },
  complained: { bg: "rgba(244, 67, 54, 0.32)", fg: "#b71c1c" },
};

const StatusPill: React.FC<{ status: SendStatus }> = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span
      style={{
        display: "inline-block",
        background: c.bg,
        color: c.fg,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {status === "blocked_dev" ? "blocked (dev)" : status}
    </span>
  );
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

interface DetailModalProps {
  rowId: string;
  onClose: () => void;
  onResend: (row: OutboundSendLogRow) => void;
}

const DetailModal: React.FC<DetailModalProps> = ({
  rowId,
  onClose,
  onResend,
}) => {
  const [row, setRow] = useState<OutboundSendLogRow | null>(null);
  const [events, setEvents] = useState<OutboundSendEventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    APIService.COMMUNICATIONS.GET_LOG_DETAIL(rowId)
      .then((data) => {
        if (!alive) return;
        setRow(data.row);
        setEvents(data.events);
      })
      .catch(() => {
        if (alive) setLoading(false);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [rowId]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 24,
          maxWidth: 720,
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>Send Detail</h3>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        {loading && <div>Loading…</div>}
        {row && (
          <>
            <div style={{ marginBottom: 12 }}>
              <strong>Channel:</strong> {row.channel} &nbsp;
              <strong>Status:</strong> <StatusPill status={row.status} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Recipient:</strong>{" "}
              {row.recipientEmail || row.recipientPhone}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Sent by:</strong> {row.senderAdminEmail}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Created:</strong> {fmtDate(row.created)}
            </div>
            {row.subject && (
              <div style={{ marginBottom: 12 }}>
                <strong>Subject:</strong> {row.subject}
              </div>
            )}
            {row.templateName && (
              <div style={{ marginBottom: 12 }}>
                <strong>Template:</strong> {row.templateName}
              </div>
            )}
            {row.errorReason && (
              <div style={{ marginBottom: 12, color: "#c62828" }}>
                <strong>Error:</strong> {row.errorReason}
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <strong>Body:</strong>
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  padding: 12,
                  marginTop: 6,
                  background: "#fafafa",
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                {row.channel === "email" ? (
                  <div dangerouslySetInnerHTML={{ __html: row.body }} />
                ) : (
                  <div style={{ whiteSpace: "pre-wrap" }}>{row.body}</div>
                )}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Delivery events ({events.length}):</strong>
              {events.length === 0 ? (
                <div
                  style={{ color: "#888", fontStyle: "italic", marginTop: 6 }}
                >
                  No webhook events received yet. Configure SendGrid Event
                  Webhook + Twilio status callback to populate this timeline.
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  {events.map((e) => (
                    <div
                      key={e.id}
                      style={{
                        padding: "6px 0",
                        borderBottom: "1px solid #eee",
                        fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{e.eventType}</span>{" "}
                      <span style={{ color: "#666" }}>
                        {fmtDate(e.eventTimestamp)}
                      </span>
                      {e.reason && (
                        <span style={{ marginLeft: 8, color: "#c62828" }}>
                          ({e.reason})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button onClick={onClose}>Close</button>
              <button
                style={{
                  background: "#ffbc17",
                  border: "none",
                  padding: "6px 16px",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
                onClick={() => onResend(row)}
              >
                Resend
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface SendLogTableProps {
  recipientFilter?: string;
  hideRecipientFilterInput?: boolean;
  onResend?: (row: OutboundSendLogRow) => void;
}

const SendLogTable: React.FC<SendLogTableProps> = ({
  recipientFilter,
  hideRecipientFilterInput = false,
  onResend,
}) => {
  const [rows, setRows] = useState<OutboundSendLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [openDetailId, setOpenDetailId] = useState<string | null>(null);

  // filters
  const [channelFilter, setChannelFilter] = useState<SendChannel | "">("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [recipientInput, setRecipientInput] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: LogQueryParams = {
      limit: PAGE_SIZE,
      offset,
    };
    if (channelFilter) params.channel = channelFilter;
    if (statusFilter) params.status = statusFilter;
    const recipient = recipientFilter || recipientInput;
    if (recipient) params.recipient = recipient;
    APIService.COMMUNICATIONS.GET_LOG(params)
      .then((data) => {
        setRows(data.rows);
        setTotal(data.total);
      })
      .catch((e) => {
        console.error("Send log load failed", e);
        setError("Failed to load send log");
      })
      .finally(() => setLoading(false));
  }, [offset, channelFilter, statusFilter, recipientFilter, recipientInput]);

  useEffect(() => {
    load();
  }, [load]);

  const handleResend = useCallback(
    (row: OutboundSendLogRow) => {
      setOpenDetailId(null);
      if (onResend) onResend(row);
    },
    [onResend]
  );

  return (
    <div>
      {!hideRecipientFilterInput && (
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <label>
            Channel:{" "}
            <select
              value={channelFilter}
              onChange={(e) =>
                setChannelFilter(e.target.value as SendChannel | "")
              }
            >
              <option value="">All</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </label>
          <label>
            Status:{" "}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="opened">Opened</option>
              <option value="clicked">Clicked</option>
              <option value="bounced">Bounced</option>
              <option value="failed">Failed</option>
              <option value="blocked_dev">Blocked (dev)</option>
            </select>
          </label>
          <label>
            Recipient contains:{" "}
            <input
              type="text"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              placeholder="email or phone"
              style={{ padding: "4px 8px" }}
            />
          </label>
          <button
            onClick={() => {
              setOffset(0);
              load();
            }}
          >
            Refresh
          </button>
        </div>
      )}
      {error && <div style={{ color: "#c62828" }}>{error}</div>}
      {loading && <div>Loading send log…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ color: "#888", padding: "16px 0" }}>No sends yet.</div>
      )}
      {!loading && rows.length > 0 && (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ddd", textAlign: "left" }}>
                <th style={{ padding: "8px 12px" }}>Date</th>
                <th style={{ padding: "8px 12px" }}>Channel</th>
                <th style={{ padding: "8px 12px" }}>Recipient</th>
                <th style={{ padding: "8px 12px" }}>Subject / Body</th>
                <th style={{ padding: "8px 12px" }}>Status</th>
                <th style={{ padding: "8px 12px" }}>Sent by</th>
                <th style={{ padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  style={{ borderBottom: "1px solid #f0f0f0", fontSize: 13 }}
                >
                  <td style={{ padding: "8px 12px" }}>{fmtDate(r.created)}</td>
                  <td style={{ padding: "8px 12px" }}>{r.channel}</td>
                  <td style={{ padding: "8px 12px" }}>
                    {r.recipientEmail || r.recipientPhone}
                  </td>
                  <td
                    style={{
                      padding: "8px 12px",
                      maxWidth: 280,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.subject || r.body.slice(0, 60)}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <StatusPill status={r.status} />
                  </td>
                  <td style={{ padding: "8px 12px", color: "#666" }}>
                    {r.senderAdminEmail}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <button
                      style={{
                        border: "none",
                        background: "rgba(33, 150, 243, 0.12)",
                        color: "#1565c0",
                        padding: "4px 12px",
                        borderRadius: 4,
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                      onClick={() => setOpenDetailId(r.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              fontSize: 13,
              color: "#666",
            }}
          >
            <div>
              Showing {offset + 1}–{Math.min(offset + rows.length, total)} of{" "}
              {total}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
      {openDetailId && (
        <DetailModal
          rowId={openDetailId}
          onClose={() => setOpenDetailId(null)}
          onResend={handleResend}
        />
      )}
    </div>
  );
};

export default SendLogTable;
